import React from 'react';
import { Building2, IndianRupee, User } from 'lucide-react';
import type { GrievanceListRow } from '../../../services/MasterAdmin/grievanceAdminService';

const formatInr = (n: number | string | null | undefined) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const round2 = (n: number) => Math.round(n * 100) / 100;

type ShareCard = {
  key: string;
  label: string;
  sublabel: string;
  pct: number;
  amount: number;
  secondaryAmount?: number | null;
  icon: React.ComponentType<{ className?: string }>;
  border: string;
  bg: string;
  accent: string;
  pctColor: string;
};

type Props = {
  g: GrievanceListRow & { resolvedAt?: string | null };
};

export const GrievanceResolutionSharesPanel: React.FC<Props> = ({ g }) => {
  const gross = Number(g.totalBookingAmount) || 0;
  const vPct = Number(g.vendorSharePct) || 0;
  const uPct = Number(g.userSharePct) || 0;
  const kPct = Number(g.vedikaSharePct) || 0;
  const pctSum = round2(vPct + uPct + kPct);

  const vendorAmt = gross > 0 ? round2((gross * vPct) / 100) : 0;
  const userAmt = gross > 0 ? round2((gross * uPct) / 100) : 0;
  const vedikaAmt = gross > 0 ? round2((gross * kPct) / 100) : 0;
  const refundRecorded = g.refundAmount != null && Number(g.refundAmount) > 0;

  const shares: ShareCard[] = [
    {
      key: 'vendor',
      label: 'Vendor',
      sublabel: 'Vendor share',
      pct: vPct,
      amount: vendorAmt,
      icon: Building2,
      border: 'border-slate-200 dark:border-slate-600',
      bg: 'bg-slate-50 dark:bg-slate-900/40',
      accent: 'text-slate-700 dark:text-slate-200',
      pctColor: 'text-slate-600 dark:text-slate-300',
    },
    {
      key: 'user',
      label: 'User refund',
      sublabel: refundRecorded ? 'Recorded refund' : 'User share',
      pct: uPct,
      amount: refundRecorded ? Number(g.refundAmount) : userAmt,
      secondaryAmount:
        refundRecorded && Math.abs(Number(g.refundAmount) - userAmt) > 0.01 ? userAmt : null,
      icon: User,
      border: 'border-emerald-200 dark:border-emerald-800',
      bg: 'bg-emerald-50 dark:bg-emerald-950/30',
      accent: 'text-emerald-800 dark:text-emerald-200',
      pctColor: 'text-emerald-700 dark:text-emerald-300',
    },
    {
      key: 'vedika',
      label: 'Vedika',
      sublabel: 'Platform share',
      pct: kPct,
      amount: vedikaAmt,
      icon: IndianRupee,
      border: 'border-primary-200 dark:border-primary-800',
      bg: 'bg-primary-50 dark:bg-primary-950/30',
      accent: 'text-primary-800 dark:text-primary-200',
      pctColor: 'text-primary-700 dark:text-primary-300',
    },
  ];

  return (
    <section className="space-y-4 rounded-xl border border-green-200 bg-green-50/40 p-4 dark:border-green-900/50 dark:bg-green-950/20 sm:p-5">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Resolution & share split</h3>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
            Gross booking: <strong className="tabular-nums">{formatInr(gross)}</strong>
            {g.resolvedAt && (
              <span className="ml-2">· Resolved {new Date(g.resolvedAt).toLocaleString()}</span>
            )}
          </p>
        </div>
        <span
          className={`inline-flex self-start rounded-full px-2.5 py-1 text-xs font-medium ${
            Math.abs(pctSum - 100) < 0.01
              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
          }`}
        >
          Total {pctSum}%
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {shares.map((s) => {
          const Icon = s.icon;
          return (
            <div
              key={s.key}
              className={`flex min-h-[120px] flex-col gap-2 rounded-lg border p-4 ${s.border} ${s.bg}`}
            >
              <div className="flex items-center gap-2">
                <Icon className={`h-4 w-4 shrink-0 ${s.accent}`} />
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${s.accent}`}>{s.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{s.sublabel}</p>
                </div>
              </div>
              <p className={`text-2xl font-bold tabular-nums ${s.pctColor}`}>{s.pct}%</p>
              <p className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">
                {formatInr(s.amount)}
              </p>
              {s.secondaryAmount != null && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Split amount: {formatInr(s.secondaryAmount)}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {g.resolutionNote && (
        <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900/50">
          <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Resolution note</p>
          <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200">{g.resolutionNote}</p>
        </div>
      )}
    </section>
  );
};
